<template>
  <div class="approvalRecord" :class="{ 'compact-approval': compact, 'ledger-readonly-approval': ledgerReadOnly }">
    <el-alert v-if="loadError" :title="loadError" type="error" :closable="false" show-icon />
    <div v-if="ledgerReadOnly && loadError" class="ledger-flow-empty"><el-button @click="getList">重新加载</el-button></div>
    <div v-if="ledgerReadOnly && recordLoading" class="ledger-flow-empty"><i class="el-icon-loading" /> 正在加载审批流程</div>
    <div v-else-if="ledgerReadOnly && !loadError && !list.length" class="ledger-flow-empty">暂无审批记录</div>
    <div v-if="showFormInfo && formInfoList.length" class="approval-form-info">
      <div v-for="(item, index) in formInfoList" :key="index" class="approval-form-item">
        <div class="approval-form-label">{{ item.label }}</div>
        <div class="approval-form-value">
          <FileUpload
            v-if="formValue(item).type === 'fileObjs'"
            v-model="formValue(item).fileObjs"
            :limit="5"
            :readOnly="true">
          </FileUpload>
          <span v-else-if="formValue(item).type === 'targetInfo'">{{ targetInfoText(formValue(item).targetInfo) }}</span>
          <span v-else>{{ formValue(item).text || '-' }}</span>
        </div>
      </div>
    </div>
    <section v-if="queryParams.workOrderId && !recordLoading && !loadError" class="approval-cc" aria-label="抄送人">
      <div class="approval-cc-label"><i class="el-icon-share" aria-hidden="true" />抄送人</div>
      <div class="approval-cc-people">
        <span v-for="user in ccUsers" :key="user.key" class="approval-cc-person">{{ user.name }}</span>
        <span v-if="!ccUsers.length" class="approval-cc-empty">无抄送人</span>
      </div>
    </section>
    <el-timeline v-show="!ledgerReadOnly || (!recordLoading && !loadError && list.length)" style="padding-right: 15px; padding-bottom: 50px; margin-top: 40px;">
      <el-timeline-item v-for="(item, index) in list" :key="index" :icon="item.icon"
                        :class="{ 'focused-comment': String(item.recordId) === String(focusRecordId), 'ledger-current-node': ledgerReadOnly && Number(item.operationType) === 4, 'ledger-upcoming-node': ledgerReadOnly && Number(item.operationType) === 6 }"
                        timestamp="" :size="item.size" placement="bottom" :color="item.color">
        <template v-if="ledgerReadOnly">
          <div class="ledger-event-heading"><strong>{{ item.isTransferRecord ? item.recordTitle : item.taskName || (Number(item.operationType) === 0 ? '发起申请' : Number(item.operationType) === 5 ? '评论' : '审批记录') }}</strong><time v-if="item.operateTime">{{ item.operateTime }}</time></div>
          <div class="ledger-event-people"><span>{{ recordUserText(item) }}</span><span v-if="Number(item.operationType) !== 6 || item.automaticApprovalExpected" class="ledger-event-result" :class="[{'automatic-past': item.automaticApproval, 'automatic-next': item.automaticApprovalExpected}, 'result-' + item.operationType]">{{ item.operationTypeStr }}</span></div>
        </template>
        <template v-else>
        <div v-if="compact && item.taskName && !item.isTransferRecord" class="approval-node-name">{{ item.taskName }}</div>
        <span v-if="item.operateTime" class="operateTimeClass">{{ item.operateTime }}</span>
        <el-row v-if="item.isTransferRecord" class="transfer-record-title">
          <span>{{ item.recordTitle }}</span>
        </el-row>
        <el-row v-else>
          <span v-for="(user, index) in item.userList" :key="index">{{ user.userName }}{{
              (item.userList.length > 0 && index < item.userList.length - 1) ? "、" : ""
            }}</span> <span v-if="!compact || Number(item.operationType) !== 6 || item.automaticApprovalExpected" :class="[item.operateTypeClass, {'automatic-past': item.automaticApproval, 'automatic-next': item.automaticApprovalExpected}]">{{ item.operationTypeStr }}</span>
        </el-row>
        </template>
        <div v-if="item.isTransferRecord" class="transfer-record-card">
          <div class="transfer-record-route">
            <div class="transfer-record-person">
              <span class="transfer-record-label">{{ item.sourceLabel }}</span>
              <strong>{{ item.recordSource }}</strong>
            </div>
            <i class="el-icon-right transfer-record-arrow"></i>
            <div class="transfer-record-person receiver">
              <span class="transfer-record-label">{{ item.targetLabel }}</span>
              <strong>{{ item.transferTarget }}</strong>
            </div>
          </div>
          <div v-if="item.transferNote" class="transfer-record-note">
            <span>{{ item.noteLabel }}</span>
            <p>{{ item.transferNote }}</p>
          </div>
        </div>
        <el-row v-else-if="item.remark" class="content" v-text="item.remark"></el-row>
        <div v-if="item.replyToUserName" class="comment-reply-to">回复 {{ item.replyToUserName }}</div>
        <div v-if="item.mentionedUserList && item.mentionedUserList.length" class="comment-mentions">
          <el-tag v-for="user in item.mentionedUserList" :key="user.userId" size="mini">@{{ user.userName }}</el-tag>
        </div>
        <el-row v-if="item.attachmentFileObjs">
          <FileUpload v-model="item.attachmentFileObjs" :limit="5" :readOnly="true"></FileUpload>
        </el-row>
        <el-button v-if="canComment && Number(item.operationType) === 5 && item.recordId" type="text" @click="replyComment(item)">回复</el-button>
      </el-timeline-item>
    </el-timeline>
    <el-row v-if="!ledgerReadOnly" class="approval-actions">
      <!-- 0-待处理 1-审批中 2-已通过 3-已驳回 4-已取消  -->
      <el-button v-if="canComment" type="default" @click="approve('comment')">评论</el-button>
      <el-button v-if="!interactionOnly && allowTransfer && hasAuth" type="default" icon="el-icon-share" @click="openTransferDialog">转交审批</el-button>
      <el-button v-if="!interactionOnly && hasAuth && unifiedApproval && (allowPass || allowReject)" type="primary" @click="approve('approval')">审批</el-button>
      <el-button v-if="!interactionOnly && hasAuth && !unifiedApproval && allowReject" type="default" @click="approve('reject')">驳回</el-button>
      <el-button v-if="!interactionOnly && hasAuth && !unifiedApproval && allowPass" type="primary" @click="approve('pass')">{{ passButtonText }}</el-button>
    </el-row>
    <el-dialog :title="title" :custom-class="approveType === 'approval' ? 'oa-review-dialog' : ''" :visible.sync="visible" :close-on-click-modal="false" :close-on-press-escape="false" :show-close="!approvalSubmitting" :width="unifiedApproval ? 'min(560px, 94vw)' : '40%'" append-to-body>
      <div slot="title" :class="{ 'oa-review-dialog-heading': approveType === 'approval' }">
        <template v-if="approveType === 'approval'"><span class="oa-review-dialog-icon"><i class="el-icon-edit-outline" aria-hidden="true" /></span><div><h3>审批</h3><p>请选择审批结果，并填写审批意见</p></div></template>
        <span v-else class="el-dialog__title">{{ title }}</span>
      </div>
      <el-form :model="queryParams" :rules="rules" ref="infoForm" :disabled="approvalSubmitting" :label-position="approveType === 'approval' ? 'top' : 'right'" :label-width="approveType === 'approval' ? null : '0'">
        <el-form-item v-if="approveType === 'approval'" label="审批结果" required>
          <el-radio-group v-model="approvalDecision" class="oa-review-decisions" aria-label="审批结果">
            <el-radio v-if="allowPass" label="pass" border class="oa-review-decision oa-review-decision--pass"><i class="el-icon-circle-check" aria-hidden="true" /><span>通过</span></el-radio>
            <el-radio v-if="allowReject" label="reject" border class="oa-review-decision oa-review-decision--reject"><i class="el-icon-circle-close" aria-hidden="true" /><span>不通过</span></el-radio>
          </el-radio-group>
        </el-form-item>
        <AmountReviewFields v-if="reviewDraft && opinionOperationType === 1" :value="reviewDraft" :disabled="approvalSubmitting" />
        <el-form-item :label="approveType === 'approval' ? '审批意见' : ''" :required="approveType === 'approval'" prop="remark">
          <ApprovalOpinionShortcuts :visible="visible && approveType !== 'comment'" :operation-type="opinionOperationType" :approval-type="approvalType" :disabled="approvalSubmitting" @select="fillApprovalOpinion" />
          <el-input type="textarea" :autosize="{ minRows: 3, maxRows: 5}" :maxlength="approveType === 'comment' ? 2000 : 500" show-word-limit v-model="queryParams.remark" :placeholder="approvalPlaceholder" clearable @input="onCommentInput" />
        </el-form-item>
        <el-form-item v-if="approveType === 'comment'">
          <el-select ref="mentionSelect" v-model="selectedMentionIds" multiple filterable :loading="participantLoading"
                     style="width:100%" placeholder="@ 流程参与人" @change="onMentionsChange">
            <el-option v-for="user in participants" :key="user.userId" :value="String(user.userId)"
                       :label="user.userName + (user.role ? '（' + user.role + '）' : '')" />
          </el-select>
          <div v-if="participantError" class="comment-reply-to">{{ participantError }} <el-button type="text" @click="loadParticipants">重试</el-button></div>
        </el-form-item>
        <el-form-item v-if="approveType === 'comment'" label="附件:" prop="attachmentFileIdList" label-width="50px">
          <FileUpload v-model="queryParams.attachmentFileIdList" :limit="5" :readOnly="false"
                      @on-change="handleFileChange"></FileUpload>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="approvalSubmitting" @click="clear">取消</el-button>
        <el-button type="primary" :loading="approvalSubmitting" @click="confirmRemark">{{ approveType === 'approval' ? '提交审批' : '确认' }}</el-button>
      </span>
    </el-dialog>
    <el-dialog title="转交本次审批" :visible.sync="transferVisible" width="520px" append-to-body :close-on-click-modal="false">
      <el-alert title="只转交当前审批节点，后续审批流程不变。" type="info" :closable="false" show-icon />
      <el-form class="transfer-form" label-width="88px">
        <el-form-item label="转交给" required>
          <el-select
            v-model="transferForm.targetUserId"
            filterable
            clearable
            style="width: 100%;"
            placeholder="搜索并选择同事"
            :loading="transferUserLoading"
          >
            <el-option
              v-for="user in transferUserOptions"
              :key="user.userId"
              :label="transferUserLabel(user)"
              :value="user.userId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="转交说明">
          <el-input v-model="transferForm.remark" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="选填，如：出差期间请代为审批" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="transferVisible = false">取消</el-button>
        <el-button type="primary" :loading="transferSubmitting" @click="confirmTransfer">确认转交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {handleWorkOrder, queryWorkOrder, addComment, transferWorkOrder} from "@/api/crm/contract";
import {listUserWithOutDataScope} from "@/api/system/user";
import UserAvatar from '@/views/system/user/userAvatar'
import FileUpload from "@/views/crm/components/fileUpload";
import { commentParticipants } from '@/api/crm/approvalMessage'
import ApprovalOpinionShortcuts from './approvalOpinionShortcuts'
import { ledgerDetail } from '@/api/crm/oaLedger'
import AmountReviewFields from './amountReviewFields'
const { reviewPayload } = require('@/utils/amount-review')

export default {
  name: "operateLogList",
  props: {
    compact: { type: Boolean, default: false },
    ledgerReadOnly: { type: Boolean, default: false },
    unifiedApproval: { type: Boolean, default: false },
    allowPass: { type: Boolean, default: true },
    targetId: {},
    targetType: {},
    focusRecordId: { default: '' },
    interactionOnly: { type: Boolean, default: false },
    showFormInfo: {
      type: Boolean,
      default: true
    },
    approvalHandler: {
      type: Function,
      default: null
    },
    passButtonText: {
      type: String,
      default: '通过'
    },
    allowTransfer: {
      type: Boolean,
      default: true
    },
    allowReject: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      list: [],
      ccUserList: [],
      listSequence: 0,
      recordLoading: false,
      loadError: '',
      participants: [],
      selectedMentionIds: [],
      participantLoading: false,
      participantError: '',
      title: '',
      approvalPlaceholder: '',
      approveType: 'pass',
      approvalDecision: '',
      autofilledOpinion: '',
      approvalSubmitting: false,
      visible: false,
      approvalType: null,
      amountReview: null,
      currentTaskId: null,
      reviewDraft: null,
      status: null,
      statusStr: '',
      hasAuth: null,
      canComment: false,
      formInfoList: [],
      transferVisible: false,
      transferSubmitting: false,
      transferUserLoading: false,
      transferUserOptions: [],
      transferForm: {
        targetUserId: null,
        remark: ''
      },
      // 查询参数
      queryParams: {
        workOrderId: null,
        needFormInfo: this.showFormInfo,
        remark: '',
        parentRecordId: null,
        mentionedUserIdList: [],
        attachmentFileIdList: []
      },
      rules: {
        // remark: [
        //   {required: true, message: '该项不能为空', trigger: 'blur'}
        // ]
      },
    };
  },
  components: {
    ApprovalOpinionShortcuts,
    AmountReviewFields,
    UserAvatar,
    FileUpload
  },
  created() {
    this.queryParams.workOrderId = this.targetId;
    this.getList();
  },
  beforeDestroy() { this.listSequence++ },
  watch: {
    opinionOperationType() {
      if (this.autofilledOpinion && this.queryParams.remark === this.autofilledOpinion) this.queryParams.remark = ''
      this.autofilledOpinion = ''
    },
    targetId (val) {
      if (val !== this.queryParams.workOrderId) {
        this.queryParams.workOrderId = val;
        this.getList();
      }
    },
    showFormInfo(val) {
      this.queryParams.needFormInfo = val
      this.getList()
    }
  },
  computed: {
    ccUsers() {
      const seen = new Set()
      return (Array.isArray(this.ccUserList) ? this.ccUserList : []).reduce((users, user, index) => {
        if (!user || typeof user !== 'object') return users
        const id = user.userId == null ? '' : String(user.userId).trim()
        const name = String(user.userName || '').trim() || (id ? `用户${id}` : '')
        const key = id ? `user-${id}` : `name-${index}`
        if (name && !seen.has(key)) { seen.add(key); users.push({ key, name }) }
        return users
      }, [])
    },
    opinionOperationType() {
      const decision = this.approveType === 'approval' ? this.approvalDecision : this.approveType
      return decision === 'pass' ? 1 : decision === 'reject' ? 2 : 0
    }
  },
  methods: {
    fillApprovalOpinion(opinion) {
      if (this.approvalSubmitting || !this.visible) return
      this.queryParams.remark = opinion
      this.autofilledOpinion = opinion
    },
    refresh () {
      return this.getList()
    },
    // 清空
    clear () {
      this.visible = false
      this.approvalDecision = ''
      this.queryParams.remark = ''
      this.queryParams.attachmentFileIdList = []
      this.queryParams.parentRecordId = null
      this.queryParams.mentionedUserIdList = []
      this.selectedMentionIds = []
      this.resetForm('infoForm')
      this.getList()
    },
    // 审批
    approve (type) {
      if (this.approvalSubmitting) return
      this.approvalDecision = ''
      this.reviewDraft = this.amountReview ? JSON.parse(JSON.stringify(this.amountReview)) : null
      this.queryParams.parentRecordId = null
      this.queryParams.mentionedUserIdList = []
      this.selectedMentionIds = []
      this.queryParams.remark = ''
      this.queryParams.attachmentFileIdList = []
      if (type === 'approval') {
        this.title = '审批'
        this.approvalPlaceholder = '请输入审批意见（500字以内）'
      } else if (type === 'pass') {
        this.title = this.passButtonText
        this.approvalPlaceholder = '请输入审批意见（500字以内）'
      } else if (type === 'reject') {
        this.title = '驳回审批'
        this.approvalPlaceholder = '请输入审批意见（500字以内）'
      } else if (type === 'comment') {
        this.title = '发布评论'
        this.approvalPlaceholder = '请输入评论内容'
      }
      this.approveType = type
      this.visible = true
      if (type === 'comment') this.loadParticipants()
    },
    replyComment(item) {
      this.approve('comment')
      this.queryParams.parentRecordId = item.recordId
      this.title = `回复${(item.userList && item.userList[0] && item.userList[0].userName) || '评论'}`
    },
    async loadParticipants() {
      this.participantLoading = true
      this.participantError = ''
      try {
        const response = await commentParticipants(this.queryParams.workOrderId)
        this.participants = response.data || []
        if (!this.participants.length) this.participantError = '当前流程暂无可@人员'
      } catch (err) { this.participantError = '流程参与人加载失败' }
      finally { this.participantLoading = false }
    },
    onMentionsChange(ids) {
      const previous = (this.queryParams.mentionedUserIdList || []).map(String)
      let text = this.queryParams.remark || ''
      this.participants.forEach(user => {
        const id = String(user.userId)
        const token = `@${user.userName}`
        if (ids.includes(id) && !previous.includes(id) && !text.includes(token)) text = text.replace(/@$/, '') + token + ' '
        if (!ids.includes(id) && previous.includes(id)) text = text.split(token + ' ').join('').split(token).join('')
      })
      this.queryParams.remark = text
      this.queryParams.mentionedUserIdList = ids.slice()
    },
    onCommentInput(text) {
      if (this.approveType !== 'comment') return
      const ids = this.selectedMentionIds.filter(id => {
        const user = this.participants.find(user => String(user.userId) === id)
        return user && text.includes(`@${user.userName}`)
      })
      this.selectedMentionIds = ids
      this.queryParams.mentionedUserIdList = ids.slice()
      if (text.endsWith('@') && this.$refs.mentionSelect) this.$refs.mentionSelect.focus()
    },
    async confirmRemark () {
      if (this.approvalSubmitting) return
      const valid = await new Promise(resolve => this.$refs.infoForm.validate(resolve))
      if (!valid || this.approvalSubmitting) return
      const decision = this.approveType === 'approval' ? this.approvalDecision : this.approveType
      if (this.approveType === 'approval') {
        if (!this.hasAuth || this.interactionOnly) return
        if (!['pass', 'reject'].includes(decision)) { this.$message.error('请选择审批结果'); return }
        if ((decision === 'pass' && !this.allowPass) || (decision === 'reject' && !this.allowReject)) {
          this.$message.error('当前节点不支持此审批结果'); return
        }
      }
      const remark = String(this.queryParams.remark || '').trim()
      if ((this.approveType === 'approval' || decision !== 'pass') && !remark) {
        this.$message.error(decision === 'comment' ? '请输入评论内容' : '请输入审批意见'); return
      }
      let amountReview
      try { if (decision === 'pass' && this.reviewDraft) amountReview = reviewPayload(this.reviewDraft) }
      catch (error) { this.$message.error(error.message); return }
      this.approvalSubmitting = true
      const loading = this.$loading({lock: true, text: '提交中', spinner: 'el-icon-loading'})
      try {
        const payload = { ...this.queryParams, remark }
        if (decision === 'comment') {
          await addComment(payload)
        } else {
          if (amountReview) payload.amountReview = amountReview
          payload.operationType = decision === 'pass' ? 1 : 2
          payload.expectedTaskId = this.currentTaskId
          await (this.approvalHandler ? this.approvalHandler(payload) : handleWorkOrder(payload))
        }
        this.$message.success(decision === 'comment' ? '评论成功' : '审批提交成功')
        this.$emit('success')
        this.clear()
      } catch (_) {
        // The request layer displays the error; keep the decision and opinion for retry.
      } finally {
        this.approvalSubmitting = false
        loading.close()
      }
    },
    // 获取审批记录
    getList() {
      const sequence = this.listSequence = (this.listSequence || 0) + 1
      this.ccUserList = []
      if (!this.queryParams.workOrderId) {
        this.recordLoading = false
        return Promise.resolve();
      }
      this.loadError = ''
      this.recordLoading = true
      const query = this.ledgerReadOnly ? ledgerDetail(this.queryParams.workOrderId) : queryWorkOrder(this.queryParams)
      return query.then(response => {
        if (sequence !== this.listSequence) return
        const {status, statusStr, hasAuth, canComment, approvalRecordDtoList, createTime, formInfoList, currentTaskName} = response.data
        const displayCurrentTaskName = currentTaskName === '采购员修改询价' ? '采购员询价' : currentTaskName
        this.$emit('update', {
          createTime,
          status,
          statusStr,
          hasAuth,
          currentTaskName: displayCurrentTaskName
        })
        this.approvalType = Number(response.data.approvalType) || null
        this.amountReview = response.data.amountReview || null
        this.currentTaskId = response.data.currentTaskId || null
        this.formInfoList = formInfoList || []
        this.ccUserList = response.data.ccUserList || []
        // 0-发起审批 1-已通过 2-已驳回 3-已取消 4-待审批 5-评论 6-后面的节点
        ;(approvalRecordDtoList || []).forEach(item => {
          const legacyRecall = Number(item.operationType) === 7 && item.remark === '采购专员撤回定商报价'
          const legacyReject = Number(item.operationType) === 7 && item.remark === '总经理退回采购专员修改'
          if (legacyRecall) {
            item.operationType = 9
            item.operationTypeStr = '撤回修改'
            item.remark = ''
          } else if (legacyReject) {
            item.operationType = 2
            item.operationTypeStr = '已驳回'
            item.remark = ''
          }
          if (item.taskName === '采购员修改询价') {
            item.taskName = '采购员询价'
          }
          if (this.targetType === 'purchaseContractApproval' && item.taskName === '用印管理员执行用印') {
            if (Number(item.operationType) === 4) item.operationTypeStr = '待办理'
            if (Number(item.operationType) === 1) item.operationTypeStr = '已办结'
          }
          item.size = 'large'
          // item.operationType = 5
          if (item.operationType === 0) {
            item.icon = 'el-icon-s-promotion'
            item.color = '#409EFF'
            item.operateTypeClass = 'createApproval'
          } else if (item.operationType === 1) {
            item.icon = 'el-icon-check'
            item.color = '#409EFF'
            item.operateTypeClass = 'passClass'
          } else if (item.operationType === 2) {
            item.icon = 'el-icon-close'
            item.color = '#ff7a10'
            item.operateTypeClass = 'reject'
          } else if (item.operationType === 3) {
            item.icon = 'el-icon-delete'
            item.color = '#aeadb1'
            item.operateTypeClass = 'cancel'
          } else if (item.operationType === 4) {
            item.icon = 'el-icon-s-check'
            item.color = '#aeadb1'
            item.operateTypeClass = 'toApproval'
          } else if (item.operationType === 5) {
            item.icon = 'el-icon-chat-line-square'
            item.color = '#24bc66'
          } else if (item.operationType === 6) {
            item.icon = 'el-icon-more'
            item.color = '#aeadb1'
          } else if (item.operationType === 7) {
            item.icon = 'el-icon-share'
            item.color = '#409EFF'
          } else if (item.operationType === 8) {
            item.icon = 'el-icon-s-claim'
            item.color = '#67C23A'
          } else if (item.operationType === 9) {
            item.icon = 'el-icon-refresh-left'
            item.color = '#E6A23C'
            item.operateTypeClass = 'cancel'
          } else if (item.operationType === 10) {
            item.icon = 'el-icon-circle-check'
            item.color = '#909399'
            item.operateTypeClass = 'cancel'
          }
          if (this.ledgerReadOnly) item.color = ({ 0: '#4480f3', 1: '#26a179', 2: '#d66666', 4: '#4480f3', 5: '#26a179', 7: '#4480f3', 8: '#26a179', 9: '#ca923e' })[Number(item.operationType)] || '#abb6c7'
          item.isTransfer = Number(item.operationType) === 7
          item.isHandover = Number(item.operationType) === 8
          item.isTransferRecord = item.isTransfer || item.isHandover
          if (item.isTransfer) {
            Object.assign(item, this.parseTransferRemark(item.remark), {
              recordTitle: '审批转交',
              sourceLabel: '转交人',
              recordSource: this.recordUserText(item),
              targetLabel: '接收人',
              noteLabel: '转交说明'
            })
          } else if (item.isHandover) {
            Object.assign(item, this.parseHandoverRemark(item.remark), {
              recordTitle: '交接',
              sourceLabel: '离职员工',
              targetLabel: '交接人',
              noteLabel: '交接说明'
            })
          }
        })
        this.list = approvalRecordDtoList || []
        this.status = status
        this.statusStr = statusStr
        this.hasAuth = this.ledgerReadOnly ? 0 : hasAuth
        this.canComment = !this.ledgerReadOnly && Number(canComment) === 1
        this.$nextTick(() => {
          const target = this.focusRecordId && this.$el.querySelector('.focused-comment')
          if (target) target.scrollIntoView({ block: 'center' })
          this.$emit('loaded', this.list)
        })
      }).catch(err => { if (sequence === this.listSequence) this.loadError = err.message || '流程加载失败，请重试' }).finally(() => { if (sequence === this.listSequence) this.recordLoading = false })
    },
    handleFileChange (fileObjs) {
      this.queryParams.attachmentFileIdList = fileObjs;
    },
    parseTransferRemark(remark) {
      const text = String(remark || '').trim()
      const matched = text.match(/^转交给([^：:]+)(?:[：:](.*))?$/)
      if (!matched) {
        return { transferTarget: '-', transferNote: text }
      }
      return {
        transferTarget: String(matched[1] || '').trim() || '-',
        transferNote: String(matched[2] || '').trim()
      }
    },
    parseHandoverRemark(remark) {
      const text = String(remark || '').trim()
      const matched = text.match(/^(.+?)交接给([^：:]+)(?:[：:](.*))?$/)
      if (!matched) {
        return { recordSource: '-', transferTarget: '-', transferNote: text }
      }
      return {
        recordSource: String(matched[1] || '').trim() || '-',
        transferTarget: String(matched[2] || '').trim() || '-',
        transferNote: String(matched[3] || '').trim()
      }
    },
    recordUserText(item) {
      const names = ((item && item.userList) || [])
        .map(user => user.userName || user.nickName || user.name)
        .filter(Boolean)
      return names.join('、') || '-'
    },
    openTransferDialog() {
      this.transferForm = { targetUserId: null, remark: '' }
      this.transferVisible = true
      if (!this.transferUserOptions.length) {
        this.loadTransferUsers()
      }
    },
    loadTransferUsers() {
      this.transferUserLoading = true
      listUserWithOutDataScope({ pageNum: 1, pageSize: 999 }).then(response => {
        const rows = (response && response.rows) || []
        this.transferUserOptions = rows.filter(user =>
          String(user.status || '0') === '0' && Number(user.tenantUserStatus || 1) === 1
        )
      }).finally(() => {
        this.transferUserLoading = false
      })
    },
    transferUserLabel(user) {
      const name = user.nickName || user.userName || `用户${user.userId}`
      const deptName = user.dept && user.dept.deptName
      return deptName ? `${name}（${deptName}）` : name
    },
    confirmTransfer() {
      if (!this.transferForm.targetUserId) {
        this.$message.error('请选择接收人')
        return
      }
      this.transferSubmitting = true
      transferWorkOrder({
        workOrderId: this.queryParams.workOrderId,
        targetUserId: this.transferForm.targetUserId,
        remark: this.transferForm.remark
      }).then(() => {
        this.$message.success('本次审批已转交')
        this.transferVisible = false
        this.$emit('success')
        this.getList()
      }).finally(() => {
        this.transferSubmitting = false
      })
    },
    formValue(item) {
      return (item && item.value) || {}
    },
    targetInfoText(targetInfo) {
      if (!targetInfo) {
        return '-'
      }
      return targetInfo.targetName || targetInfo.name || '-'
    }
  }
};
</script>

<style lang='scss' scoped>
.approvalRecord {
  .approval-cc { display:flex; align-items:flex-start; gap:12px; margin:16px 0 22px; padding:12px 14px; border-radius:6px; background:#f6f8fb; font-size:13px; line-height:24px; }
  .approval-cc-label { flex:none; color:#738198; }
  .approval-cc-label i { margin-right:6px; }
  .approval-cc-people { display:flex; flex:1; min-width:0; flex-wrap:wrap; gap:6px 8px; }
  .approval-cc-person { max-width:100%; padding:0 8px; border:1px solid #e3e9f1; border-radius:4px; background:#fff; color:#34465e; overflow-wrap:anywhere; }
  .approval-cc-empty { color:#8491a5; }
  .focused-comment { background: #edf5ff; border-radius: 4px; }
  .comment-reply-to { color: #909399; font-size: 12px; margin: 6px 0; }
  .comment-mentions .el-tag { margin: 0 8px 6px 0; }
  .approval-form-info {
    margin: 10px 15px 0 0;
    padding: 12px 14px;
    background: #f8fafc;
    border: 1px solid #e6ebf2;
    border-radius: 4px;
  }

  .approval-form-item {
    display: flex;
    align-items: flex-start;
    line-height: 24px;
    padding: 6px 0;
    border-bottom: 1px solid #eef2f7;
  }

  .approval-form-item:last-child {
    border-bottom: 0;
  }

  .approval-form-label {
    flex: 0 0 96px;
    color: #606266;
  }

  .approval-form-value {
    flex: 1;
    color: #303133;
    word-break: break-all;
  }

  ::v-deep.el-timeline-item__node--large {
    left: -7px;
    width: 24px;
    height: 24px;
  }

  ::v-deep.el-timeline-item__timestamp.is-top {
    height: 17px !important;
  }

  ::v-deep.el-timeline-item__icon {
    font-size: 15px;
  }

  ::v-deep.el-timeline-item__content {
    padding-top: 10px;
  }

  ::v-deep.el-timeline-item {
    padding-bottom: 5px;
  }

  .operateTimeClass {
    position: absolute;
    right: 16px;
  }

  .createApproval {
    margin-left: 30px;
    color: #424242;
  }

  .passClass {
    margin-left: 30px;
    color: #0070ff;
  }

  .toApproval {
    margin-left: 30px;
    color: #ff7700;
  }

  .reject {
    margin-left: 30px;
    color: #ff0303;
  }

  .cancel {
    margin-left: 30px;
    color: #ff0303;
  }

  .avatar {
    float: left;
  }
  .content {
    padding: 10px 0px;
    color: #8a8989;
  }

  .transfer-record-title {
    span {
      display: inline-block;
      padding: 3px 9px;
      border-radius: 4px;
      background: #ecf4ff;
      color: #3a7ce3;
      font-size: 12px;
      font-weight: 600;
      line-height: 20px;
    }
  }

  .transfer-record-card {
    overflow: hidden;
    margin: 10px 0 14px;
    border: 1px solid #dfe7f2;
    border-radius: 6px;
    background: #f9fbfe;
  }

  .transfer-record-route {
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 14px 16px;
  }

  .transfer-record-person {
    flex: 1;
    min-width: 0;

    span,
    strong {
      display: block;
    }

    strong {
      margin-top: 4px;
      overflow-wrap: anywhere;
      color: #303d4f;
      font-size: 14px;
      line-height: 20px;
    }

    &.receiver {
      text-align: right;
    }
  }

  .transfer-record-label {
    color: #909baa;
    font-size: 12px;
    line-height: 18px;
  }

  .transfer-record-arrow {
    flex: none;
    color: #409eff;
    font-size: 18px;
  }

  .transfer-record-note {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 10px 16px;
    border-top: 1px solid #e5ebf3;
    background: #fff;

    span {
      flex: none;
      color: #909baa;
      font-size: 12px;
      line-height: 20px;
    }

    p {
      flex: 1;
      min-width: 0;
      margin: 0;
      overflow-wrap: anywhere;
      color: #5d6c80;
      font-size: 13px;
      line-height: 20px;
    }
  }

  .approval-actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-bottom: 50px;

    .el-button + .el-button {
      margin-left: 0;
    }
  }

  .transfer-form {
    margin-top: 20px;
  }
}
</style>

<style scoped>
.compact-approval ::v-deep .el-timeline{padding:0 0 6px 3px!important;margin:4px 0 0!important}.compact-approval ::v-deep .el-timeline-item{padding-bottom:22px}.compact-approval ::v-deep .el-timeline-item__content{line-height:1.7;overflow-wrap:anywhere;font-size:13px}.compact-approval .approval-node-name{font-size:14px;font-weight:600;color:#30465e;margin-bottom:5px}.compact-approval .operateTimeClass{position:static;right:auto;float:none;display:block;max-width:100%;margin-bottom:4px;font-size:12px;line-height:1.6;color:#8a97a8}.compact-approval .content{margin-top:6px;font-size:13px;white-space:pre-wrap}.compact-approval .approval-actions{display:flex;flex-wrap:wrap;gap:8px;border-top:1px solid #edf0f5;padding-top:14px}.compact-approval .approval-actions .el-button{margin-left:0}
</style>

<style scoped lang="scss">
.approvalRecord {
  .ledger-event-result.automatic-past, .automatic-past { color:#237b5a; background:#eaf7f0; padding:2px 8px; border-radius:5px; }
  .ledger-event-result.automatic-next, .automatic-next { color:#316bcb; background:#eef5ff; border:1px dashed #7ca7ec; padding:2px 8px; border-radius:5px; }
}
.ledger-readonly-approval {
  .ledger-flow-empty { padding:48px 16px; text-align:center; color:#8491a5; font-size:14px; }
  ::v-deep .el-timeline { padding:4px 0 0 10px!important; margin:0!important; }
  ::v-deep .el-timeline-item { padding-bottom:18px; }
  ::v-deep .el-timeline-item:last-child { padding-bottom:0; }
  ::v-deep .el-timeline-item__tail { border-color:#dce4ef; top:20px; }
  ::v-deep .el-timeline-item__node--large { top:16px; width:26px; height:26px; left:-8px; box-shadow:0 0 0 4px #f5f7fb; }
  ::v-deep .el-timeline-item__wrapper { padding-left:30px; top:0; }
  ::v-deep .el-timeline-item__content { padding:16px 18px; background:#fff; border:1px solid #e5ebf3; border-radius:10px; line-height:1.65; }
  ::v-deep .ledger-current-node .el-timeline-item__content { border-color:#bbd2fc; background:#f8fbff; }
  ::v-deep .ledger-upcoming-node .el-timeline-item__content { background:#fafbfd; }
  .ledger-event-heading { display:flex; align-items:baseline; justify-content:space-between; flex-wrap:wrap; gap:4px 14px; }
  .ledger-event-heading strong { color:#30435e; font-size:14px; font-weight:600; overflow-wrap:anywhere; }
  .ledger-event-heading time { color:#8a97a9; font-size:12px; white-space:nowrap; }
  .ledger-event-people { display:flex; align-items:center; flex-wrap:wrap; gap:8px 12px; margin-top:8px; color:#53627a; font-size:13px; }
  .ledger-event-result { padding:2px 8px; border-radius:5px; font-size:12px; color:#738197; background:#f0f3f7; }
  .result-0,.result-4,.result-7 { color:#316bcb; background:#e9f1ff; }
  .result-1,.result-8 { color:#237b5a; background:#eaf7f0; }
  .result-2 { color:#bd5050; background:#fff0f0; }
  .result-9 { color:#9c7427; background:#fff5df; }
  .content { margin:12px 0 0; padding:10px 12px; border-radius:6px; background:#f5f7fa; font-size:13px; color:#65758b; white-space:pre-wrap; overflow-wrap:anywhere; }
  .transfer-record-card { margin-bottom:0; }
  @media(max-width:600px) { ::v-deep .el-timeline-item__content { padding:12px; }.ledger-event-heading time { width:100%; } }
}
</style>

<style lang="scss">
// Dialog is appended to body; scope these styles to its unique custom class.
.oa-review-dialog {
  overflow: hidden;
  border-radius: 14px;
  box-shadow: 0 20px 70px rgba(28, 44, 72, .2);
  .el-dialog__header { padding: 26px 28px 22px; border-bottom: 1px solid #edf0f5; }
  .el-dialog__headerbtn { top: 24px; right: 24px; font-size: 19px; }
  .el-dialog__body { padding: 24px 28px 26px; }
  .el-dialog__footer { padding: 18px 28px; border-top: 1px solid #edf0f5; background: #f8fafc; }
  .oa-review-dialog-heading { display: flex; align-items: center; gap: 14px; padding-right: 22px; }
  .oa-review-dialog-icon { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; flex-shrink: 0; border-radius: 12px; background: #edf3ff; color: #4480f3; font-size: 23px; }
  .oa-review-dialog-heading h3 { margin: 0 0 6px; color: #2d3e55; font-size: 19px; font-weight: 600; line-height: 1.4; }
  .oa-review-dialog-heading p { margin: 0; color: #8693a6; font-size: 13px; line-height: 1.5; }
  .el-form-item { margin-bottom: 26px; }
  .el-form-item:last-child { margin-bottom: 0; }
  .el-form-item__label { padding-bottom: 10px; color: #52627a; font-size: 14px; font-weight: 500; line-height: 22px; }
  .oa-review-decisions { display: flex; gap: 14px; width: 100%; }
  .oa-review-decision.el-radio.is-bordered { display: flex; align-items: center; flex: 1; height: 62px; min-width: 0; margin: 0; padding: 0 18px; border: 1px solid #e2e7ef; border-radius: 9px; background: #fff; transition: background .15s, border-color .15s; }
  .oa-review-decision .el-radio__label { display: flex; align-items: center; gap: 8px; padding-left: 12px; font-size: 15px; font-weight: 500; color: #53627a; }
  .oa-review-decision .el-radio__label i { font-size: 20px; }
  .oa-review-decision--pass .el-radio__label i { color: #26a580; }
  .oa-review-decision--reject .el-radio__label i { color: #db7373; }
  .oa-review-decision:hover { border-color: #adc8fb!important; background: #f8faff!important; }
  .oa-review-decision.is-checked { border-color: #4480f3!important; background: #f1f6ff!important; }
  .oa-review-decision:focus-within { outline: 2px solid #b7cffc; outline-offset: 2px; }
  .oa-review-decision.is-checked .el-radio__label { color: #326bd0; }
  .el-textarea__inner { min-height: 132px!important; padding: 13px 15px 29px; border-color: #e0e6ef; border-radius: 9px; color: #35465e; line-height: 1.8; font-size: 14px; }
  .el-textarea__inner:hover { border-color: #b7c6da; }
  .el-textarea__inner:focus { border-color: #4480f3; box-shadow: 0 0 0 3px rgba(68,128,243,.08); }
  .el-textarea .el-input__count { right: 14px; bottom: 9px; color: #9aa6b7; font-size: 12px; }
  .dialog-footer { display: flex; justify-content: flex-end; gap: 12px; }
  .dialog-footer .el-button { min-width: 96px; margin: 0; height: 40px; border-radius: 7px; }
  .dialog-footer .el-button--primary { min-width: 116px; }
}
@media (max-width: 600px) {
  .oa-review-dialog .el-dialog__header { padding: 22px 20px 18px; }
  .oa-review-dialog .el-dialog__body { padding: 22px 20px; }
  .oa-review-dialog .el-dialog__footer { padding: 16px 20px; }
  .oa-review-dialog .oa-review-decisions { gap: 10px; }
  .oa-review-dialog .oa-review-decision.el-radio.is-bordered { padding: 0 12px; }
}
</style>
